import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  TemplateRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { WarningIconComponent, ClockIconComponent } from '@kuiper/icons';
import {
  COLLABORATED_TASKS_FILTER_OPTIONS,
  COLLABORATED_TASKS_INSIGHTS,
  COLLABORATED_TASKS_ON_HOLD_COLUMNS,
  COLLABORATED_TASKS_ON_HOLD_TABLE_FILTER_TABS,
  COLLABORATED_TASKS_VIEW_DETAILS_ACTION,
} from './collaborated-tasks.constants';
import { COLUMN_IDENTIFIERS } from '../../const';
import { FilterTableColByFeatFlagPipe } from '../../../../common/pipes/filter-table-col-by-featFlag/filter-table-col-by-feat-flag.pipe';
import { isFeatureFlaggedColumnVisible } from '../../../../common/helpers/feature-flagged-table-columns/feature-flagged-table-columns.helper';
import { RelatedRequestsDropdownComponent } from '../../../../../ui-components/lib/related-requests-dropdown/related-requests-dropdown.component';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AvatarComponent } from '../../../../../ui-components/lib/avatar/avatar.component';
import { BadgeComponent } from '../../../../../ui-components/lib/badge/badge.component';
import { ToasterService } from '../../../../../ui-components/services/toast/toast.service';
import { ExportService } from '../../../../../ui-components/services/export/export.service';
import { TruncatePipe } from '../../../../../ui-components/pipes/truncate/truncate.pipe';
import { ModalServiceService } from '../../../../../ui-components/lib/modal/modal-service.service';
import { TrimTooltipDirective } from '../../../../../ui-components/directives/trim-tooltip/trim-tooltip.directive';
import { UserDropdownViewComponent } from '../../../../../ui-components/lib/user-dropdown-view/user-dropdown-view.component';
import { FilterOption } from '../../../../../ui-components/lib/filter-dropdown/filter-dropdown.model';
import { FilterMapping } from '../../../../../ui-components/lib/filter-dropdown/filter-mapping.enum';
import { MyInboxService } from '../../../../common/services/my-inbox/my-inbox.service';
import { ActionListComponent } from '../../../../common/components/action-list/action-list.component';
import { RequestCreatorComponent } from '../../../../common/components/request-creator/request-creator.component';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { TenantPathPipe } from '../../../../common/pipes/tenant-path/tenant-path.pipe';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { StepStatusPipe } from '../../../../common/pipes/step-status/step-status.pipe';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../ui-components/lib/table/table.model';
import { RequestTypeEnum } from '../../../../common/enums/request-type';
import { StepStatusEnum } from '../../../../common/enums/step-status';
import { ArchiveConfirmationPopupComponent } from '../../../../common/components/archive-confirmation-popup/archive-confirmation-popup.component';
import { CreatedRequest } from '../../../../common/models/created-by-me.model';
import { Insight } from '../../../../common/models/insight.model';
import { ActionList } from '../../../../common/models/action-list.model';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
} from '../../../../common/constants/pagination.constants';
import {
  ApiResponse,
  Meta,
} from '../../../../common/models/api-response.model';
import { EMPLOYEE_ROUTES } from '../../../../common/constants/employee.routing.constants';
import { SlaDirective } from '../../../../common/directives/sla/sla.directive';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import { FilteredTableComponent } from '../../../../common/components/filtered-table/filtered-table.component';
import { FilteredTablePayload } from '../../../../common/components/filtered-table/filtered-table.model';
import { takeUntilDestroyed } from '../contributed-tasks/contributed-tasks.imports';

@Component({
  selector: 'lib-collaborated-tasks',
  imports: [
    DatePipe,
    BadgeComponent,
    StepStatusPipe,
    SlaDirective,
    TranslocoPipe,
    AvatarComponent,
    TruncatePipe,
    UserDropdownViewComponent,
    ActionListComponent,
    TrimTooltipDirective,
    ReactiveFormsModule,
    WarningIconComponent,
    ClockIconComponent,
    RequestCreatorComponent,
    RouterLink,
    TenantPathPipe,
    FilterTableColByFeatFlagPipe,
    FilteredTableComponent,
    ArchiveConfirmationPopupComponent,
    RelatedRequestsDropdownComponent,
  ],
  templateUrl: './collaborated-tasks.component.html',
  styleUrl: './collaborated-tasks.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CollaboratedTasksComponent implements OnInit {
  readonly pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  readonly EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;
  protected readonly COLUMN_IDENTIFIERS = COLUMN_IDENTIFIERS;

  private readonly modalService = inject(ModalServiceService);
  private readonly myInboxService = inject(MyInboxService);
  private readonly transloco = inject(TranslocoService);
  protected readonly parallelStepStatus = StepStatusEnum;
  private readonly router = inject(Router);
  private readonly featureFlagService = inject(FeatureFlagsService);
  private readonly toasterService = inject(ToasterService);
  private readonly exportService = inject(ExportService);
  private readonly tenantService = inject(TenantService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly showParentTasksColumn = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.INBOX.parent_wf_column
    )
  );

  protected readonly showRelatedRequestsColumn = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.parent_and_child_approach
    )
  );

  /******** pagination ********/
  meta = signal<Meta | undefined>(undefined);

  /*********** modal *********/
  archiveModal = viewChild<TemplateRef<unknown>>('archive');
  restoreModal = viewChild<TemplateRef<unknown>>('restore');
  selectedRequestId = signal<number>(0);

  /*********** table  *********/
  insights = signal<Insight[]>(COLLABORATED_TASKS_INSIGHTS);
  isLoading = signal<boolean>(false);
  dataSource = signal<CreatedRequest[]>([]);

  readonly columns = computed<TableColumns[]>(() =>
    COLLABORATED_TASKS_ON_HOLD_COLUMNS.filter(col =>
      isFeatureFlaggedColumnVisible(col.header, {
        showParentTasksColumn: this.showParentTasksColumn(),
        showRelatedRequestsColumn: this.showRelatedRequestsColumn(),
      })
    ).map(column => {
      return {
        ...column,
        header: this.transloco.translate(column.header),
        showViewIcon: column.showViewIcon,
        viewIconRouterLink: column.viewIconRouterLink
          ? row => column.viewIconRouterLink(row, this.tenantService)
          : undefined,
      };
    })
  );

  tableFilterTabs = signal<TableFilterTab[]>(
    COLLABORATED_TASKS_ON_HOLD_TABLE_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );

  activeTableFilterTab = computed(
    () => this.payload().tab ?? this.tableFilterTabs()[0]
  );
  payload = signal<FilteredTablePayload>({
    page: 1,
    size: DEFAULT_PAGE_SIZE,
    sortColumn: {
      key: '',
      value: 'asc',
    },
    search: '',
    filters: {},
    tab: this.tableFilterTabs()[0],
  });

  viewDetailsAction: ActionList = {
    ...COLLABORATED_TASKS_VIEW_DETAILS_ACTION,
    label: this.transloco.translate(
      COLLABORATED_TASKS_VIEW_DETAILS_ACTION.label
    ),
  };

  counterRefresh = output<string>();
  actionSourceTabName = input<string>();

  filterOptions: FilterOption[] = COLLABORATED_TASKS_FILTER_OPTIONS.map(
    option => ({
      ...option,
      label: this.transloco.translate(option.label),
      placeholder: option.placeholder
        ? this.transloco.translate(option.placeholder)
        : '',
    })
  );
  filterMapping: Record<string, string> = {
    workflowTypes: FilterMapping.WORKFLOW_TYPES_FLOWS,
    creationDateStart: FilterMapping.CREATED_AFTER_FLOWS,
    creationDateEnd: FilterMapping.CREATED_BEFORE_FLOWS,
    projectIds: FilterMapping.PROJECT_IDS,
  };

  ngOnInit(): void {
    this.getInsights();
  }

  getInsights() {
    const keys = [
      StepStatusEnum.INPROGRESS,
      StepStatusEnum.COMPLETED,
      StepStatusEnum.REJECTED,
      StepStatusEnum.ON_HOLD,
    ];
    this.myInboxService
      .getCollaboratedTasksInsight()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: { data: Insight[] }) => {
          keys.forEach((key, index) => {
            this.insights()[index].value =
              res.data
                .find((insight: Insight) => insight.key === key)
                ?.value?.toString() ?? '0';
          });
          this.insights.set([...this.insights()]);
        },
      });
  }

  checkCalledApi() {
    this.checkReturnedObservable().subscribe({
      next: (res: ApiResponse<CreatedRequest>) =>
        this.handleCollaboratedTasksSuccess(res),
      error: () => {
        this.isLoading.set(false);
      },
    });
  }

  checkReturnedObservable = () => {
    return this.activeTableFilterTab().key === 'archived'
      ? this.getArchiveRequests$().pipe(takeUntilDestroyed(this.destroyRef))
      : this.getCollaboratedTasks$().pipe(takeUntilDestroyed(this.destroyRef));
  };

  getCollaboratedTasks$() {
    this.isLoading.set(true);
    this.meta.set(undefined);
    const { page, size, sortColumn, search, filters } = this.payload();
    if (filters[FilterMapping.PROJECT_STATE] === 'all')
      delete filters[FilterMapping.PROJECT_STATE];

    return this.myInboxService.getCollaboratedTasks(
      page,
      size,
      this.activeTableFilterTab().key,
      sortColumn,
      search,
      filters
    );
  }

  getArchiveRequests$() {
    this.isLoading.set(true);
    this.meta.set(undefined);
    const { page, size, sortColumn, search, filters } = this.payload();
    delete filters[FilterMapping.PROJECT_STATE];

    return this.myInboxService.getFilteredArchive(
      page,
      size,
      'assigned_to_me',
      sortColumn,
      0,
      1,
      search,
      filters
    );
  }

  handleCollaboratedTasksSuccess(res: ApiResponse<CreatedRequest>) {
    this.isLoading.set(false);
    this.meta.set(undefined);
    const data = res.data.map((item: CreatedRequest) => ({
      ...item,
      assignees: item.current_assignees.map(assignee => assignee.data),
      assigneeType: item.current_assignees.map(assignee => assignee.type),
    }));
    this.dataSource.set(data);
    this.meta.set(res.meta);
  }

  protected getRelatedRequestViewLink = (childFlow: { id: number }) =>
    this.tenantService.tenantPath(
      `${EMPLOYEE_ROUTES.MY_INBOX.REQUEST}/${RequestTypeEnum.VIEW}/${childFlow.id}`
    );

  viewRequest(id: number) {
    this.router.navigate([
      this.tenantService.tenantPath(
        `${EMPLOYEE_ROUTES.MY_INBOX.REQUEST}/${RequestTypeEnum.VIEW}/${id.toString()}`
      ),
    ]);
  }

  getStatusActionList(requestId: number) {
    const viewDetailsActionWithId: ActionList = {
      ...this.viewDetailsAction,
      routerLinks: [
        this.tenantService.tenantPath(
          `${EMPLOYEE_ROUTES.MY_INBOX.REQUEST}/${RequestTypeEnum.VIEW}/${requestId.toString()}`
        ),
      ],
    };

    return [viewDetailsActionWithId];
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  submitExportForm(form: FormGroup) {
    const formValue = form.value;
    this.myInboxService
      .exportCollaboratedTasks(formValue)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: unknown) => {
          this.exportService.handleExportSuccess(
            response as Response,
            form,
            () => this.closeModal()
          );
        },
        error: (error: { error?: { message?: string } }) => {
          this.toasterService.add(
            error.error?.message ?? '',
            '',
            '',
            'error',
            true,
            true
          );
          this.exportService.reInitExportForm(form);
          this.closeModal();
        },
      });
  }

  archiveRequest() {
    this.isLoading.set(true);
    this.closeModal();
    this.myInboxService
      .archiveRequest(this.selectedRequestId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.checkCalledApi();
          this.getInsights();
          this.counterRefresh.emit('collaborated_tasks');
        },
        error: (error: { message?: string }) => {
          this.toasterService.add(
            error?.message ?? '',
            '',
            '',
            'error',
            true,
            true
          );
          this.isLoading.set(false);
        },
      });
  }

  restoreRequest() {
    this.isLoading.set(true);
    this.closeModal();
    this.myInboxService
      .restoreRequest(this.selectedRequestId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.checkCalledApi();
          this.getInsights();
          this.counterRefresh.emit('collaborated_tasks');
        },
        error: (error: { message?: string }) => {
          this.toasterService.add(
            error?.message ?? '',
            '',
            '',
            'error',
            true,
            true
          );
          this.isLoading.set(false);
        },
      });
  }
}
